<script setup lang="ts">
import {
    LCircleMarker,
    LControl,
    LIcon,
    LMap,
    LMarker,
    LTileLayer
} from '@vue-leaflet/vue-leaflet'
import type { LatLngBoundsExpression, LatLngTuple, PointTuple } from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { type PropType, ref, watch, onMounted, onBeforeMount } from 'vue'
import type {
    Cluster,
    MapBounds,
    MapKeyGroup,
    Marker
} from '../../../interfaces/map'
import MapKey from '../map/MapKey.vue'
import MapZoom from '../map/MapZoom.vue'

const props = defineProps({
    tilesUrl: {
        type: String,
        default: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
    },
    markerList: {
        type: Array as PropType<Array<Marker | Cluster>>,
        required: true
    },
    defaultSelectedMarker: {
        type: Object as PropType<Marker>,
        default: undefined
    },
    /** If this isn't set, we use Geolocalisation to center the map or fallback on Strasbourg */
    mapCenter: {
        type: Object as PropType<LatLngTuple>,
        default: undefined
    },
    mapZoom: {
        type: Number,
        default: 9
    },
    centerOnClickedMarker: {
        type: Boolean,
        default: true
    },
    drawCircleAroundMarker: {
        type: Boolean,
        default: true
    },
    hideControls: {
        type: Boolean,
        default: false
    },
    minZoom: {
        type: Number,
        default: 0
    },
    maxZoom: {
        type: Number,
        default: 18
    },
    keyGroups: {
        type: Array as PropType<MapKeyGroup[]>,
        default: () => []
    },
    keyTitle: {
        type: String,
        default: ''
    },
    externalBounds: {
        type: Object as PropType<LatLngTuple>,
        default: undefined
    }
})

const emit = defineEmits([
    'markerClicked',
    'centerUpdated',
    'boundsUpdated',
    'zoomUpdated'
])

const mapRef = ref<InstanceType<typeof LMap> | undefined>(undefined)

const zoom = ref(1)
const center = ref<PointTuple>([48.5734053, 7.7521113])
const bounds = ref<MapBounds | undefined>(undefined)

const circleMarker = ref({
    center: undefined as LatLngTuple | undefined,
    radius: 28,
    color: '#B1C3D4'
})
const selectedMarker = ref<Marker | undefined>(undefined)
const localizationCookie = ref<LatLngTuple | undefined>(undefined)
const memoizedBounds = ref<MapBounds | undefined>(undefined)

const isMapKeyOpen = ref(false)
const loading = ref(true)

const markerIsCluster = (marker: Marker | Cluster) => marker.type === 'cluster'
const getCluster = (marker: Marker | Cluster) => marker as Cluster

/**
 * @name centerUpdated
 * @description Emits centerUpdated when the center changes
 *
 * @param {number[]} centerCoordinates Center coordinates sent by the leaflet map
 */
const centerUpdated = (centerCoordinates: number[]) => {
    emit('centerUpdated', centerCoordinates)
}

/**
 * @name boundsUpdated
 * @description Emits boundsUpdated when the bounds change
 *
 * @param {MapBounds} boundsCoordinates Bounds coordinates sent by the leaflet map
 */
const boundsUpdated = (boundsCoordinates: MapBounds) => {
    if (
        JSON.stringify(boundsCoordinates) !==
        JSON.stringify(memoizedBounds.value)
    ) {
        memoizedBounds.value = boundsCoordinates
        emit(
            'boundsUpdated',
            boundsCoordinates,
            // We inverse the bounds for the API needs
            inverseBounds(boundsCoordinates)
        )
    }
}

/**
 * @name zoomUpdated
 * @description Emits zoomUpdated when the bounds change
 *
 * @param {number} zoomValue Zoom value sent by the leaflet map
 */
const zoomUpdated = (zoomValue: number) => {
    emit('zoomUpdated', zoomValue)
}

/**
 * @name markerClicked
 * @description Emits zoomUpdated when the bounds change
 *
 * @param {Marker} marker Zoom value sent by the leaflet map
 */
const markerClicked = (marker: Marker) => {
    selectedMarker.value = marker
    emit('markerClicked', marker)
}

const getPositionSuccess = (position: GeolocationPosition) => {
    // Timeout to let the map charge before changing center
    setTimeout(() => {
        center.value = [position.coords.latitude, position.coords.longitude]
    }, 250)

    if (!localizationCookie.value) {
        sessionStorage.setItem(
            'geolocalisation',
            [position.coords.latitude, position.coords.longitude].toString()
        )

        localizationCookie.value = [
            position.coords.latitude,
            position.coords.longitude
        ]
    }
}

const getPositionFailed = () => {
    // Timeout to let the map charge before changing center
    if (localizationCookie.value) {
        setTimeout(() => {
            center.value = localizationCookie.value as PointTuple
        }, 50)
    }
}

/**
 * @name zoomIn
 * @description Zooms in the map
 */
const zoomIn = () => {
    if (props.maxZoom > zoom.value) {
        zoom.value += 1
    }
}

/**
 * @name zoomOut
 * @description Zooms out the map
 */
const zoomOut = () => {
    if (props.minZoom < zoom.value) {
        zoom.value -= 1
    }
}

const inverseBounds = (bounds: MapBounds) => ({
    NW: {
        lat: bounds._northEast.lat,
        lng: bounds._southWest.lng
    },
    SE: {
        lat: bounds._southWest.lat,
        lng: bounds._northEast.lng
    }
})

const updateIsMapKeyOpen = (isOpen: boolean) => {
    isMapKeyOpen.value = isOpen
}

const onSelectedMarkerChange = (newMarker: Marker | undefined) => {
    // Used as the default behavior when selecting a marker
    if (props.centerOnClickedMarker && newMarker) {
        center.value = newMarker.latLng as PointTuple

        if (markerIsCluster(newMarker) && zoom.value < props.maxZoom) {
            setTimeout(() => {
                zoom.value += 1
            }, 250)
        }

        if (props.drawCircleAroundMarker && !markerIsCluster(newMarker)) {
            circleMarker.value.center = newMarker.latLng
        }
    }
}

onBeforeMount(() => {
    const geolocalisation = sessionStorage.getItem('geolocalisation')

    if (geolocalisation) {
        localizationCookie.value = geolocalisation
            .split(',')
            .map((value: any) => parseFloat(value)) as LatLngTuple
    }

    // If there's no default centering point, we center on the location of the user
    if (props.mapCenter && !props.defaultSelectedMarker) {
        center.value = props.mapCenter as PointTuple
    } else if (props.centerOnClickedMarker && !props.defaultSelectedMarker) {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                getPositionSuccess,
                getPositionFailed
            )
        }
    }

    if (props.centerOnClickedMarker && props.defaultSelectedMarker) {
        selectedMarker.value = props.defaultSelectedMarker
        center.value = props.defaultSelectedMarker.latLng as PointTuple
        circleMarker.value.center = props.defaultSelectedMarker.latLng
    }
})

onMounted(() => {
    // This is here so that we trigger a boundsUpdated emit when the map is loaded
    setTimeout(() => {
        zoom.value = props.mapZoom
    }, 200)
})

watch(selectedMarker, (newMarker) => {
    onSelectedMarkerChange(newMarker)
})

watch(
    () => props.markerList,
    (newList) => {
        if (loading.value && newList.length > 0) {
            selectedMarker.value = props.defaultSelectedMarker
            loading.value = false
        }
    }
)

watch(
    () => props.externalBounds,
    (newBounds) => {
        if (newBounds) {
            mapRef.value?.leafletObject?.fitBounds([newBounds], {
                maxZoom: 14
            })
        }
    }
)
</script>

<template>
    <div
        class="w-full h-full relative"
        :class="isMapKeyOpen && 'overflow-hidden'"
    >
        <LMap
            ref="mapRef"
            v-model:zoom="zoom"
            v-model:bounds="bounds"
            v-model:center="center"
            :min-zoom="minZoom"
            :max-zoom="maxZoom"
            :use-global-leaflet="false"
            class="z-10"
            :options="{ zoomControl: false }"
            @update:center="centerUpdated"
            @update:bounds="boundsUpdated"
            @update:zoom="zoomUpdated"
            @click="isMapKeyOpen = false"
        >
            <LTileLayer
                :url="tilesUrl"
                attribution="<a href='http://jawg.io' title='Tiles Courtesy of Jawg Maps' target='_blank'>&copy; <b>Jawg</b>Maps</a> &copy; <a href='https://www.openstreetmap.org/copyright'>OpenStreetMap</a> contributors"
            />
            <LControl v-if="!hideControls" position="bottomright">
                <MapZoom @zoom-in="zoomIn" @zoom-out="zoomOut" />
                <MapKey
                    v-model="isMapKeyOpen"
                    :key-groups
                    :title="keyTitle"
                    @toggleIsOpen="updateIsMapKeyOpen"
                />
            </LControl>
            <LCircleMarker
                v-if="circleMarker.center"
                :lat-lng="circleMarker.center"
                :radius="circleMarker.radius"
                :fill-color="circleMarker.color"
                :fill-opacity="0.8"
                :stroke="false"
            />
            <LMarker
                v-for="marker in markerList"
                v-show="markerList"
                :key="marker.id"
                :lat-lng="marker.latLng"
                @click="markerClicked(marker)"
            >
                <LIcon
                    v-if="!markerIsCluster(marker)"
                    :icon-url="marker.icon"
                    :icon-size="[40, 40]"
                />
                <LIcon v-else>
                    <div class="h-8 w-8">
                        <img
                            :src="marker.icon"
                            class="-m-4 size-8"
                            alt="Cluster"
                        />
                        <div class="absolute flex -m-4 size-8">
                            <span class="m-auto text-white text-base">
                                {{ getCluster(marker).count }}
                            </span>
                        </div>
                    </div>
                </LIcon>
            </LMarker>
        </LMap>
    </div>
</template>
